import { getAccessToken } from "./auth";

const DEFAULT_PAGE_SIZE = 200;

async function fetchPicklistWithAdmin(url) {
  const token = await getAccessToken();
  const response = await fetch(url, {
    method: "GET",
    headers: {
      //"Authorization": "Basic " + btoa("prabhudasu:root"),
      "Authorization": `Bearer ${token}`,
      "Accept": "application/json",
      "Content-Type": "application/json"
    },
    credentials: "omit" 
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

function getFetchConfig() {
  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": window.Liferay?.authToken || "",
    },
    credentials: "include",
  };
}

function buildCollectionUrl(objectName, { filter, page, pageSize, sort } = {}) {
  const params = new URLSearchParams();

  if (page) {
    params.set("page", String(page));
  }

  if (pageSize) {
    params.set("pageSize", String(pageSize));
  }

  if (sort) {
    params.set("sort", sort);
  }

  if (filter) {
    params.set("filter", filter);
  }

  const queryString = params.toString();
  return queryString
    ? `/o/c/${objectName}?${queryString}`
    : `/o/c/${objectName}`;
}

async function fetchJson(url) {
  const response = await fetch(url, getFetchConfig());

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export async function fetchAllObjectEntries(objectName, options = {}) {
  let currentPage = 1;
  let lastPage = 1;
  const allItems = [];

  do {
    const url = buildCollectionUrl(objectName, {
      page: currentPage,
      pageSize: options.pageSize || DEFAULT_PAGE_SIZE,
      filter: options.filter || "",
      sort: options.sort || "",
    });

    const data = await fetchJson(url);
    const items = Array.isArray(data.items) ? data.items : [];

    allItems.push(...items);
    lastPage = Number(data.lastPage || 1);
    currentPage += 1;
  } while (currentPage <= lastPage);

  return allItems;
}

// export async function fetchPicklistEntries(externalReferenceCode) {
//   const definitionUrl =
//     "/o/headless-admin-list-type/v1.0/list-type-definitions/" +
//     `by-external-reference-code/${externalReferenceCode}`;

//   const definition = await fetchJson(definitionUrl);

//   if (Array.isArray(definition.listTypeEntries)) {
//     return definition.listTypeEntries;
//   }

//   const entriesUrl =
//     `${definitionUrl}/list-type-entries?pageSize=${DEFAULT_PAGE_SIZE}`;
//   const entriesResponse = await fetchJson(entriesUrl);

//   return Array.isArray(entriesResponse.items) ? entriesResponse.items : [];
// }

export async function fetchPicklistEntries(externalReferenceCode) {
  try {
    const definition = await fetchPicklistWithAdmin(
      `/o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/${externalReferenceCode}`
    );

    if (!definition) {
      return [];
    }

    if (Array.isArray(definition.listTypeEntries) && definition.listTypeEntries.length > 0) {
      return definition.listTypeEntries;
    }

    if (!definition.id) return [];

    let currentPage = 1;
    let lastPage = 1;
    const allItems = [];

    do {
      const url = `/o/headless-admin-list-type/v1.0/list-type-definitions/${definition.id}/list-type-entries?page=${currentPage}&pageSize=${DEFAULT_PAGE_SIZE}`;

      const data = await fetchPicklistWithAdmin(url);
      if (!data) break;

      const items = Array.isArray(data.items) ? data.items : [];
      allItems.push(...items);

      lastPage = Number(data.lastPage || 1);
      currentPage++;
    } while (currentPage <= lastPage);

    return allItems;
  } catch (error) {
    console.error("Picklist fetch failed:", error);
    return [];
  }
}
function sortByLabel(options) {
  return [...options].sort((left, right) =>
    String(left.label || "").localeCompare(String(right.label || ""))
  );
}

function uniqueByValue(options = []) {
  const seen = new Set();

  return (Array.isArray(options) ? options : []).filter((item) => {
    const key = String(item?.value || "").trim();

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function isActiveCollegeOrCourseOption(option = {}) {
  const activeValue =
    option?.isActive ??
    option?.isactive ??
    option?.active ??
    option?.is_active;

  return Number(activeValue) === 1;
}

function filterApprovedAndActiveCollegeOrCourseOptions(options = []) {
  return (Array.isArray(options) ? options : []).filter(
    (item) =>
      Number(item?.status?.code) === 0 &&
      isActiveCollegeOrCourseOption(item)
  );
}

async function fetchSearchEntries(objectName, searchValue, sortField = "") {
  if (!objectName) return [];
  if (searchValue === null || searchValue === undefined || searchValue === "") {
    return [];
  }

  const allItems = [];
  let page = 1;
  let pageSize = null;

  while (true) {
    let url = `/o/c/${objectName}/?page=${page}`;

    if (pageSize) {
      url += `&pageSize=${pageSize}`;
    }

    if (sortField) {
      url += `&sort=${sortField}:asc`;
    }

    url += `&search=${encodeURIComponent(String(searchValue))}`;

    const response = await fetch(url, getFetchConfig());
    if (!response.ok) {
      throw new Error(`Failed to fetch ${objectName} by search: ${response.status}`);
    }

    const data = await response.json();
    if (!pageSize) {
      pageSize = data?.pageSize || DEFAULT_PAGE_SIZE;
    }

    allItems.push(...(Array.isArray(data?.items) ? data.items : []));

    if (page >= Number(data?.lastPage || 1)) {
      break;
    }

    page += 1;
  }

  return allItems;
}

function getMatchingCollegeCourseMapping(mappingRows = [], selectedCourseId) {
  if (!Array.isArray(mappingRows) || mappingRows.length === 0) {
    return null;
  }

  if (!selectedCourseId) {
    return mappingRows[0];
  }

  const normalizedSelectedCourseId = String(selectedCourseId).trim();

  return (
    mappingRows.find((item) =>
      [
        item?.r_course_c_courseId,
        item?.r_course_c_courseid,
        item?.courseId,
        item?.r_course_c_course?.id,
      ]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map((value) => String(value).trim())
        .includes(normalizedSelectedCourseId)
    ) || mappingRows[0]
  );
}

const BENEFIT_FEE_TYPE_CONFIG = {
  tuitionFee: {
    fallbackId: 1,
    names: ["tuition fees", "tuition fee"],
    emptyValue: 0,
  },
  developmentFee: {
    fallbackId: 2,
    names: ["development fee", "development fees"],
    emptyValue: "",
  },
  examFee: {
    fallbackId: 3,
    names: ["exam fee", "exam fees", "examination fee"],
    emptyValue: 0,
  },
  admissionFee: {
    fallbackId: 5,
    names: ["admission fee", "admission fees"],
    emptyValue: "",
  },
  sessionFee: {
    fallbackId: 6,
    names: ["session fee", "session fees"],
    emptyValue: "",
  },
  laboratoryFee: {
    fallbackId: 7,
    names: ["laboratory fee", "laboratory fees", "lab fee", "lab fees"],
    emptyValue: "",
  },
  semesterFee: {
    fallbackId: 8,
    names: ["semester fee", "semester fees"],
    emptyValue: "",
  },
  libraryFee: {
    fallbackId: 9,
    names: ["library fee", "library fees"],
    emptyValue: "",
  },
  gymkhanaFee: {
    fallbackId: 10,
    names: ["gymkhana fee", "gymkhana fees"],
    emptyValue: "",
  },
  studyTour: {
    fallbackId: 12,
    names: ["study tour", "study tour fee", "study tour fees"],
    emptyValue: "",
  },
  enrollmentFee: {
    fallbackId: 13,
    names: [
      "enrollment fee",
      "enrollment fees",
      "enrolment fee",
      "enrolment fees",
    ],
    emptyValue: "",
  },
  otherFee: {
    fallbackId: 14,
    names: ["other fee", "other fees"],
    emptyValue: "",
  },
  projectFee: {
    fallbackId: 15,
    names: ["project fee", "project fees"],
    emptyValue: "",
  },
};

const DEFAULT_BENEFIT_FEE_DETAILS = Object.fromEntries(
  Object.entries(BENEFIT_FEE_TYPE_CONFIG).map(([key, config]) => [
    key,
    config.emptyValue,
  ])
);

let benefitFeeTypeIdCache = null;

function getBenefitFeeAcademicYearFrom() {
  return Math.max(0, new Date().getFullYear() - 1);
}

function getPositiveNumericValue(...values) {
  const match = values.find((value) => Number(value) > 0);
  return Number(match) || 0;
}

function normalizeFeeTypeName(item = {}) {
  return String(item?.name || item?.namell || "")
    .trim()
    .toLowerCase();
}

function getFeeTypeIdValue(item = {}) {
  return Number(item?.feeTypeID ?? item?.feeTypeId) || 0;
}

function resolveBenefitFeeTypeId(items = [], activeApprovedItems = [], config = {}) {
  const names = Array.isArray(config?.names) ? config.names : [];
  const fallbackId = Number(config?.fallbackId) || 0;

  const activeMatch = activeApprovedItems.find((item) =>
    names.includes(normalizeFeeTypeName(item))
  );

  if (activeMatch) {
    return getPositiveNumericValue(getFeeTypeIdValue(activeMatch), fallbackId);
  }

  const anyMatch = items.find((item) => names.includes(normalizeFeeTypeName(item)));

  if (anyMatch) {
    return 0;
  }

  return fallbackId;
}

async function fetchBenefitFeeTypeIds() {
  if (benefitFeeTypeIdCache) {
    return benefitFeeTypeIdCache;
  }

  try {
    const items = await fetchAllObjectEntries("feetypes", {
      sort: "name:asc",
    });

    const activeApprovedItems = filterApprovedAndActiveCollegeOrCourseOptions(items);

    benefitFeeTypeIdCache = Object.fromEntries(
      Object.entries(BENEFIT_FEE_TYPE_CONFIG).map(([key, config]) => [
        `${key}TypeId`,
        resolveBenefitFeeTypeId(items, activeApprovedItems, config),
      ])
    );

    return benefitFeeTypeIdCache;
  } catch (error) {
    console.error("Failed to fetch fee type ids:", error);
    return Object.fromEntries(
      Object.entries(BENEFIT_FEE_TYPE_CONFIG).map(([key, config]) => [
        `${key}TypeId`,
        Number(config?.fallbackId) || 0,
      ])
    );
  }
}

async function fetchCollegeCourseMappingsByCollegeId(collegeId) {
  const collegeIdClean = String(collegeId || "").trim();

  if (!collegeIdClean) {
    return [];
  }

  return fetchAllObjectEntries("collegecoursemappings", {
    filter: `r_college_c_collegeId eq '${collegeIdClean}'`,
    sort: "courseId:asc",
  });
}

function resolveCollegeCourseIdFromMapping(mapping = {}) {
  return getPositiveNumericValue(
    mapping?.collegeCourseId,
    mapping?.collegecourseid,
    mapping?.collegecourseId,
    mapping?.r_collegeCourse_c_collegeCourseId,
    mapping?.r_collegeCourse_c_collegecourseid
  );
}

async function fetchCollegeCourseFeeAmounts({
  collegeCourseId,
  academicYearFrom = getBenefitFeeAcademicYearFrom(),
  courseFeeYear = 1,
  feeTypeIds = [],
}) {
  const normalizedCollegeCourseId = Number(collegeCourseId) || 0;
  const normalizedAcademicYearFrom = Number(academicYearFrom) || 0;
  const normalizedCourseFeeYear = Number(courseFeeYear) || 0;
  const normalizedFeeTypeIds = [
    ...new Set(
      (Array.isArray(feeTypeIds) ? feeTypeIds : [])
        .map((feeTypeId) => Number(feeTypeId) || 0)
        .filter((feeTypeId) => feeTypeId > 0)
    ),
  ];

  if (
    !normalizedCollegeCourseId ||
    !normalizedAcademicYearFrom ||
    !normalizedCourseFeeYear ||
    normalizedFeeTypeIds.length === 0
  ) {
    return new Map();
  }

  const feeTypeFilter = normalizedFeeTypeIds
    .map((feeTypeId) => `feeTypeId eq ${feeTypeId}`)
    .join(" or ");

  const rows = await fetchAllObjectEntries("collegecoursefees", {
    filter:
      `collegeCourseId eq ${normalizedCollegeCourseId} and ` +
      `courseFeeYear eq ${normalizedCourseFeeYear} and ` +
      `academicYearFrom eq ${normalizedAcademicYearFrom} and ` +
      `(${feeTypeFilter})`,
  });

  const filteredRows = filterApprovedAndActiveCollegeOrCourseOptions(rows);
  const feeAmounts = new Map();

  filteredRows.forEach((row) => {
    const feeTypeId = Number(row?.feeTypeId ?? row?.feeTypeID) || 0;
    const feeAmount = Number(row?.feeAmount) || 0;

    if (!feeTypeId || feeAmount <= 0 || feeAmounts.has(feeTypeId)) {
      return;
    }

    feeAmounts.set(feeTypeId, feeAmount);
  });

  return feeAmounts;
}

function getBenefitFeeAmountByTypeId(
  feeAmountMap,
  feeTypeId,
  emptyValue = ""
) {
  const normalizedFeeTypeId = Number(feeTypeId) || 0;

  if (!normalizedFeeTypeId || !(feeAmountMap instanceof Map)) {
    return emptyValue;
  }

  const feeAmount = Number(feeAmountMap.get(normalizedFeeTypeId)) || 0;
  return feeAmount > 0 ? feeAmount : emptyValue;
}

export async function fetchDepartmentOptions() {
  const items = await fetchAllObjectEntries("departments", {
    sort: "name:asc",
  });

  return sortByLabel(
    items
      .filter((item) => item?.status?.code === 0 && item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
        typeofscheme: String(item.typeofscheme || "")
          .trim()
          .toUpperCase(),
        code: String(item.code || "").trim(),
        departmentId: String(item.departmentId || "").trim(),
      }))
  );
}

export async function fetchSchemeTypeOptions() {
  const items = await fetchAllObjectEntries("typeofschemes", {
    sort: "schemeType:asc",
  });

  return sortByLabel(
    items
      .filter((item) => item?.schemeType || item?.name || item?.code)
      .map((item) => ({
        value: String(item.schemeType || item.name || item.code).trim(),
        label: String(item.schemeType || item.name || item.code).trim(),
        code: String(item.code || "").trim().toUpperCase(),
      }))
  );
}

export async function fetchCasteCategoryOptions() {
  const items = await fetchAllObjectEntries("castecategories");

  return sortByLabel(
    items
      .filter(
        (item) =>
          item?.status?.code === 0 &&
          item?.isActive !== false &&
          item?.name
      )
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
        casteCategoryId: String(item.casteCategoryId || item.id),
      }))
  );
}

export async function fetchGenderOptions() {
  const items = await fetchPicklistEntries("DBT-GENDER-PICKLIST");

  return sortByLabel(
    items.map((item) => ({
      value: String(item.key || item.name),
      label: String(item.name || item.key || ""),
    }))
  );
}

export async function fetchReligionOptions() {
  const items = await fetchPicklistEntries("DBT-RELIGION-PICKLIST");

  return sortByLabel(
    items.map((item) => ({
      value: String(item.key || item.name),
      label: String(item.name || item.key || ""),
    }))
  );
}

export async function fetchCourseTypeOptions() {
  const items = await fetchAllObjectEntries("qualificationtypes", {
    filter: "isActive eq 1",
    sort: "name:asc",
  });

  return sortByLabel(
    items
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.courseTypeId || item.id),
        label: item.name,
      }))
  );
}

export async function fetchInstituteTypeOptions() {
  const items = await fetchPicklistEntries("INSTITUTE_TYPE_01");

  return sortByLabel(
    items.map((item) => ({
      value: String(item.key || item.name),
      label: String(item.name || item.key || ""),
    }))
  );
}

export async function fetchCourseTypePicklist() {
  const items = await fetchPicklistEntries("Course_Type_Picklist");

  return sortByLabel(
    items.map((item) => ({
      value: String(item.key || item.name),
      label: String(item.name || item.key || "").trim(),
    }))
  );
}

export async function fetchStateOptions() {
  const items = await fetchAllObjectEntries("states", {
    sort: "name:asc",
  });

  return sortByLabel(
    items
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: item.name,
      }))
  );
}

export async function fetchDistrictOptionsByState(stateId) {
  if (!stateId) return [];

  const stateIdClean = String(stateId).trim();
  if (!stateIdClean) return [];

  const items = await fetchAllObjectEntries("districts", {
    filter: `r_state_c_stateId eq '${stateIdClean}'`,
    sort: "name:asc",
  });

  return sortByLabel(
    (items || [])
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
        districtCode: String(
          item?.districtCode ||
            item?.districtcode ||
            item?.districtCodeCensus ||
            item?.districtcodecensus ||
            ""
        ).trim(),
      }))
  );
}

export async function fetchDivisionNameByDistrictCode(districtCode) {
  const districtCodeClean = String(districtCode || "").trim();

  if (!districtCodeClean) {
    return "";
  }

  try {
    const items = await fetchSearchEntries("divisionmappings", districtCodeClean, "name");
    const normalizedItems = Array.isArray(items) ? items : [];

    const matchedDivision =
      normalizedItems.find(
        (item) =>
          Number(item?.status?.code) === 0 &&
          String(
            item?.districtCode ||
              item?.districtcode ||
              item?.r_districtDivision_c_district?.districtCode ||
              item?.r_districtDivision_c_district?.districtcode ||
              ""
          ).trim() === districtCodeClean
      ) ||
      normalizedItems.find(
        (item) =>
          String(
            item?.districtCode ||
              item?.districtcode ||
              item?.r_districtDivision_c_district?.districtCode ||
              item?.r_districtDivision_c_district?.districtcode ||
              ""
          ).trim() === districtCodeClean
      ) ||
      normalizedItems.find((item) => Number(item?.status?.code) === 0) ||
      normalizedItems[0];

    return String(matchedDivision?.name || "").trim();
  } catch (error) {
    console.error("Failed to fetch division mapping by district code:", error);
    return "";
  }
}

export async function fetchTalukaOptionsByDistrict(districtId) {
  if (!districtId) return [];

  const districtIdClean = String(districtId).trim();
  if (!districtIdClean) return [];

  const items = await fetchAllObjectEntries("talukas", {
    filter: `r_district_c_districtId eq '${districtIdClean}'`,
    sort: "name:asc",
  });

  return sortByLabel(
    (items || [])
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
      }))
  );
}

const DEPARTMENT_COLLEGE_FLAG_MAP = {
  DHED: "flgDhe",
  DTED: "flgDte",
  DMER: "flgDmer",
  DOAR: "flgDOA",
  PWDW: "flgDisability",
  AGRD: "flgAgr",
  ANHD: "flgMUHS",
  SESD: "flgSe",
  SJSA: "flgSj",
  TRBL: "flgTribal",
  SDEE: "flgSKILL",
};

export async function fetchCollegeOptionsByTaluka({
  stateId,
  districtId,
  talukaId,
  departmentCode,
  pageSize = DEFAULT_PAGE_SIZE,
} = {}) {
  const talukaIdClean = String(talukaId || "").trim();
  const districtIdClean = String(districtId || "").trim();
  const stateIdClean = String(stateId || "").trim();
  const departmentCodeClean = String(departmentCode || "")
    .trim()
    .toUpperCase();
  const departmentFlagKey = DEPARTMENT_COLLEGE_FLAG_MAP[departmentCodeClean];

  if (!talukaIdClean && !districtIdClean) {
    return [];
  }

  function appendDepartmentFilter(filter) {
    if (!departmentCodeClean || !departmentFlagKey) {
      return filter;
    }

    return `${filter} and ((departmentAssignedCode eq '${departmentCodeClean}') or (${departmentFlagKey} eq 1))`;
  }

  async function fetchCollegeEntries(filters = []) {
    for (const filter of filters) {
      try {
        const items = await fetchAllObjectEntries("colleges", {
          filter,
          sort: "name:asc",
          pageSize,
        });

        if (Array.isArray(items) && items.length > 0) {
          return items;
        }
      } catch (error) {
        console.warn("College fetch filter failed:", filter, error);
      }
    }

    return [];
  }

  const baseFilters = talukaIdClean
    ? [`r_talukaCollege_c_talukaId eq '${talukaIdClean}'`]
    : [
        `r_districtCollege_c_districtId eq '${districtIdClean}'`,
        `r_district_c_districtMasterId eq '${districtIdClean}'`,
        `r_district_c_districtMasterid eq '${districtIdClean}'`,
        `r_district_c_districtId eq '${districtIdClean}'`,
        `r_district_c_id eq '${districtIdClean}'`,
        `districtId eq '${districtIdClean}'`,
      ];

  const items = await fetchCollegeEntries(baseFilters.map(appendDepartmentFilter));

  const mapped = uniqueByValue(
    (Array.isArray(items) ? items : [])
      // .filter((item) => {
      //   if (!departmentIdClean) {
      //     return true;
      //   }

      //   return (
      //     String(
      //       item?.departmentId ||
      //         item?.r_departmentCollege_c_departmentId ||
      //         item?.r_department_c_departmentId ||
      //         ""
      //     ).trim() === departmentIdClean
      //   );
      // })
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
        collegeType: String(item.collegeType || "").trim(),
        instituteRegionId:
          Number(
            item?.instituteRegionID ??
              item?.instituteRegionId ??
              item?.r_instituteRegion_c_instituteRegionId
          ) || 0,
        departmentId: String(
          item?.departmentId ||
            item?.r_departmentCollege_c_departmentId ||
            item?.r_department_c_departmentId ||
            ""
        ).trim(),
        departmentCode: String(item?.departmentCode || "").trim(),
        departmentName: String(item?.departmentName || "").trim(),
        stateId: String(item.stateId || item.r_state_c_stateId || stateIdClean || "").trim(),
        districtId: String(
          item.districtId ||
            item.r_districtCollege_c_districtId ||
            item.r_district_c_districtId ||
            districtIdClean ||
            ""
        ).trim(),
        talukaId: String(
          item.talukaId || item.r_talukaCollege_c_talukaId || talukaIdClean || ""
        ).trim(),
      }))
  );

  return sortByLabel(mapped);
}

export async function fetchCollegeById(collegeId) {
  const collegeIdClean = String(collegeId || "").trim();
  if (!collegeIdClean) {
    return null;
  }

  try {
    return await fetchJson(`/o/c/colleges/${encodeURIComponent(collegeIdClean)}`);
  } catch (error) {
    console.error("Failed to fetch college by id:", error);
    return null;
  }
}

export async function fetchCoursesForCollegeId(collegeId) {
  if (!collegeId) return [];

  const collegeIdClean = String(collegeId).trim();

  try {
    const mappings = await fetchCollegeCourseMappingsByCollegeId(
      collegeIdClean
    );

    const relatedCourseIds = [
      ...new Set(
        (Array.isArray(mappings) ? mappings : [])
          .map(
            (item) =>
              item?.r_course_c_courseId ??
              item?.r_course_c_courseid ??
              item?.courseId
          )
          .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
          .map((id) => String(id).trim())
      ),
    ];

    if (relatedCourseIds.length > 0) {
      const courseRows = await Promise.all(
        relatedCourseIds.map((courseId) =>
          fetchSearchEntries("courses", courseId, "name")
        )
      );

      const courseData = courseRows.flat();

      if (Array.isArray(courseData) && courseData.length > 0) {
        const normalizedCourses = courseData
          .map((course) => {
            const normalizedId = course?.id ?? course?.courseId;
            if (
              normalizedId === null ||
              normalizedId === undefined ||
              normalizedId === ""
            ) {
              return null;
            }

            return {
              ...course,
              id: String(normalizedId),
              name:
                course?.name ||
                course?.programName ||
                course?.streamName ||
                String(normalizedId),
            };
          })
          .filter(Boolean);

        const uniqueById = Array.from(
          new Map(
            normalizedCourses.map((course) => [String(course.id), course])
          ).values()
        ).sort((a, b) =>
          String(a?.name || "").localeCompare(String(b?.name || ""))
        );

        const filteredCourses =
          filterApprovedAndActiveCollegeOrCourseOptions(uniqueById);

        return sortByLabel(
          uniqueByValue(
            (filteredCourses.length > 0 ? filteredCourses : uniqueById).map(
              (item) => ({
                value: String(item?.id ?? "").trim(),
                label: String(
                  item?.name ||
                    item?.courseName ||
                    // item?.programName ||
                    item?.streamName ||
                    ""
                ).trim(),
                name: String(item?.name || "").trim(),
                courseName: String(item?.courseName || "").trim(),
                // programName: String(item?.programName || "").trim(),
                streamName: String(item?.streamName || "").trim(),
                sjsaGroupingDisability: String(
                  item?.sjsaGroupingDisability ?? ""
                ).trim(),
                courseGroupingTribal: String(
                  item?.courseGroupingTribal ?? ""
                ).trim(),
                courseGroupingSC: String(item?.courseGroupingSC ?? "").trim(),
                courseGroupingVjnt: String(
                  item?.courseGroupingVjnt ?? ""
                ).trim(),
                isProfessional: item?.isProfessional,
              })
            )
          ).filter((item) => item.value && item.label)
        );
      }
    }
  } catch (error) {
    console.warn("Course mapping lookup failed for courses API:", error);
  }

  return [];
}

export async function fetchBenefitFeeDetails(collegeId, courseId) {
  const collegeIdClean = String(collegeId || "").trim();
  if (!collegeIdClean) {
    return { ...DEFAULT_BENEFIT_FEE_DETAILS };
  }

  try {
    const mappings = await fetchCollegeCourseMappingsByCollegeId(
      collegeIdClean
    );

    const matchedMapping = getMatchingCollegeCourseMapping(mappings, courseId);
    const collegeCourseId = resolveCollegeCourseIdFromMapping(matchedMapping);

    if (!collegeCourseId) {
      return { ...DEFAULT_BENEFIT_FEE_DETAILS };
    }

    const feeTypeIds = await fetchBenefitFeeTypeIds();
    const feeAmountMap = await fetchCollegeCourseFeeAmounts({
      collegeCourseId,
      feeTypeIds: Object.values(feeTypeIds),
    });

    return {
      tuitionFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.tuitionFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.tuitionFee.emptyValue
      ),
      developmentFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.developmentFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.developmentFee.emptyValue
      ),
      examFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.examFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.examFee.emptyValue
      ),
      admissionFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.admissionFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.admissionFee.emptyValue
      ),
      sessionFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.sessionFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.sessionFee.emptyValue
      ),
      laboratoryFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.laboratoryFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.laboratoryFee.emptyValue
      ),
      semesterFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.semesterFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.semesterFee.emptyValue
      ),
      libraryFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.libraryFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.libraryFee.emptyValue
      ),
      gymkhanaFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.gymkhanaFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.gymkhanaFee.emptyValue
      ),
      studyTour: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.studyTourTypeId,
        BENEFIT_FEE_TYPE_CONFIG.studyTour.emptyValue
      ),
      enrollmentFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.enrollmentFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.enrollmentFee.emptyValue
      ),
      otherFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.otherFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.otherFee.emptyValue
      ),
      projectFee: getBenefitFeeAmountByTypeId(
        feeAmountMap,
        feeTypeIds.projectFeeTypeId,
        BENEFIT_FEE_TYPE_CONFIG.projectFee.emptyValue
      ),
    };
  } catch (error) {
    console.error("Failed to fetch college fee details:", error);
    return { ...DEFAULT_BENEFIT_FEE_DETAILS };
  }
}

export async function fetchBeneficiaryCategoryOptions() {
  return [
    { value: "Hosteller", label: "Hosteller" },
    { value: "Day Scholar", label: "Day Scholar" },
  ];
}

export async function fetchSchemeConfiguratorOptions(departmentId) {
  if (!departmentId) return [];

  const items = await fetchAllObjectEntries("schemeconfigurators", { 
    filter: `department eq '${departmentId}' and schemeStatus eq 'active'`,
    sort: "schemeName:asc",
  });

  return items
    .filter((item) => item?.schemeName)
    .map((item) => ({
      value: String(item.id),
      label: String(item.schemeName).trim(),
      schemeCode: item.schemeCode,
      drl: item.drl,
    }));
}

export async function fetchBenefitsCalculation(payload) {
  try {
    const response = await fetch(
     `/api/benefits/user/benefits-calculation`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch benefits");
    }

    return await response.json();
  } catch (error) {
    console.error("Benefits API error:", error);
    throw error;
  }
}
